import { Autocomplete, Box, Button, DialogActions, DialogContent, DialogTitle, Drawer, FormControl, FormLabel, Grid, Input, ModalClose, Radio, RadioGroup, Textarea, Typography } from "@mui/joy";
import React from "react";
import { Elevator } from "../Interfaces/Elevator";
import { Mechanic } from "../Interfaces/Mechanic";
import { TicketType, Util } from "../Helpers/Util";
import { DatePicker } from "@mui/x-date-pickers";
import { Dialog } from "@mui/material";
import { useRole } from "../Contexts/RoleContext";

interface TicketUploadDialogProps {
	open: boolean;
	close: Function;
	elevators: Array<Elevator>;
	buildingId: string;
	ticket: UploadTicket;
	mechanics: Array<Mechanic>;
}

export interface UploadTicket {
	elevatorId: string | undefined;
	ticketTypesIds: Array<string>;
	mechanicId: string | undefined;
	buildingId: string | undefined;
	ticketDate: Date | undefined;
	workPerformed: string;
	ticketsId: string;
	ticketNumber: number | undefined;
	submitted: boolean | undefined;
	buildingName: string;
}

export default function TicketUploadDialog(props: TicketUploadDialogProps) {
	const [open, setOpen] = React.useState(false);
	const [ticket, setTicket] = React.useState(props.ticket);
	const uploadRef = React.useRef<HTMLInputElement>(null);
	const [hasFile, setHasFile] = React.useState(false);
	const [showFileAttached, setShowFileAttached] = React.useState(false);
	const [elevators, setElevators] = React.useState<Array<Elevator>>([]);
	const [isPrint, setIsPrint] = React.useState(false);
	const { effectiveRole } = useRole();

	React.useEffect(() => {
		setOpen(props.open);
		setTicket({ ...props.ticket, buildingId: props.buildingId });
		setHasFile(!!props.ticket.ticketsId);
	}, [props.open, props.ticket]);

	React.useEffect(() => {
		setElevators(getElevators());
	}, [props.buildingId, props.elevators]);

	const close = () => {
		props.close();
	};

	const sizes = {
		xs: 12,
		sm: 6,
		md: 4,
		lg: 3,
	};

	const threeSizes = {
		sm: 12,
		md: 6,
		lg: 4,
	};

	const getElevators = () => {
		const buildingId = props.buildingId;
		let elevators = [] as Array<Elevator>;
		if (!Util.isNullOrEmpty(buildingId)) {
			return props.elevators.filter((x) => x.buildings_id.toLowerCase() === buildingId?.toLowerCase());
		}

		return elevators;
	};

	const openPdf = () => {
		fetch("Ticket/GetPDFTicket", {
			method: "Post",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				ticketsId: ticket.ticketsId,
				ticketNumber: ticket.ticketNumber,
			}),
		})
			.then((x) => x.text())
			.then((x) => {
				var tab = window.open() as any;
				tab.location.href = document.location.origin + "/pdfs/" + x;
			});
	};

	const save = (submit: boolean) => {
		if (!ticket.mechanicId) {
			alert("A mechanic is required.");
			return;
		}
		if (!ticket.elevatorId) {
			alert("An elevator is required.");
			return;
		}
		if (!ticket.ticketDate) {
			alert("A ticket date is required");
			return;
		}
		if (uploadRef.current && uploadRef.current.files?.length === 0) {
			alert("One file is required.");
			return;
		}

		const fd = new FormData() as any;
		fd.append("file", (uploadRef.current as any).files[0]);
		fd.append("bldg", props.buildingId);
		fd.append("elevator", ticket.elevatorId);

		fd.append("ticketDate", Util.Formatter(ticket.ticketDate, "D") ?? "");

		fd.append("mechanicId", ticket.mechanicId);
		fd.append("workPerformed", ticket.workPerformed ?? "");
		fd.append("ticketType", ticket.ticketTypesIds);
		if (ticket.ticketsId) fd.append("ticketsId", ticket.ticketsId);

		fd.append("submit", submit || false);

		fetch("Ticket/SavePDFTicket", {
			method: "POST",
			body: fd,
		}).then(close);
	};

	const printTicket = () => {
		setIsPrint(true);
		window.setTimeout(() => {
			window.print();
			window.setTimeout(() => {
				setIsPrint(false);
			}, 200);
		}, 1000);
	};

	const render = () => {
		return (
			<>
				{!isPrint && <ModalClose onClick={close} />}
				<DialogTitle>{props.ticket && props.ticket.ticketsId ? props.ticket.buildingName + " - Ticket #" + props.ticket.ticketNumber : "New Paper Ticket"}</DialogTitle>
				<DialogContent sx={{ padding: 2 }}>
					<Grid container spacing={2} sx={{ overflowX: "hidden" }}>
						<Grid xs={12}>
							<FormLabel sx={{ typography: "title-md", fontWeight: "bold" }}>Ticket Informations</FormLabel>
						</Grid>
						<Grid {...threeSizes}>
							<FormControl>
								<FormLabel>Ticket Date</FormLabel>
								<DatePicker
									disabled={ticket.submitted || effectiveRole !== "Admin"}
									slotProps={{
										textField: { variant: "standard", size: "small" },
									}}
									value={ticket.ticketDate}
									format="MM/dd/yyyy"
									onChange={(value) => {
										setTicket({ ...ticket, ticketDate: value as Date });
									}}
								/>
							</FormControl>
						</Grid>
						<Grid {...threeSizes}>
							<FormControl>
								<FormLabel>Elevator</FormLabel>
								<Autocomplete
									disabled={ticket.submitted || effectiveRole !== "Admin"}
									options={elevators}
									getOptionLabel={(opt: Elevator) => opt.serial_number}
									openOnFocus
									value={props.elevators.find((x) => x.elevators_id === ticket.elevatorId) || null}
									onChange={(event, value) => {
										setTicket({ ...ticket, elevatorId: value?.elevators_id });
									}}
								/>
							</FormControl>
						</Grid>
						<Grid {...threeSizes}>
							<FormControl>
								<FormLabel>Mechanic</FormLabel>
								<Autocomplete
									disabled={ticket.submitted || effectiveRole !== "Admin"}
									options={props.mechanics}
									getOptionLabel={(opt: Mechanic) => opt.full_name}
									openOnFocus
									value={props.mechanics.find((x) => x.users_id === ticket.mechanicId) || null}
									onChange={(event, value) => {
										setTicket({ ...ticket, mechanicId: value?.users_id });
									}}
								/>
							</FormControl>
						</Grid>
						<Grid {...threeSizes}>
							<FormControl>
								<FormLabel>Ticket Type</FormLabel>
								<Autocomplete
									disabled={ticket.submitted || effectiveRole !== "Admin"}
									options={Util.ticketTypes}
									getOptionLabel={(opt: TicketType) => opt.description}
									openOnFocus
									multiple={true}
									value={Util.ticketTypes.filter((x) => ticket.ticketTypesIds.includes(x.ticket_type_id))}
									onChange={(event, value) => {
										setTicket({ ...ticket, ticketTypesIds: value.map((x) => x.ticket_type_id) });
									}}
								/>
							</FormControl>
						</Grid>
						<Grid {...threeSizes} sx={{ display: ticket.submitted || effectiveRole !== "Admin" ? "none" : "" }}>
							<Button sx={{ width: "100%", mt: 3 }} onClick={() => uploadRef.current && uploadRef.current.click()}>
								Select File
							</Button>
							<input
								ref={uploadRef}
								onChange={() => {
									setHasFile(false);
									setShowFileAttached(true);
								}}
								style={{ display: "none" }}
								type="file"
							/>
						</Grid>
						<Grid {...threeSizes} sx={{ display: hasFile && !isPrint ? "" : "none" }}>
							<Button sx={{ width: "100%", mt: 3 }} onClick={openPdf}>
								Open File
							</Button>
						</Grid>
						{showFileAttached && (
							<Typography level="body-lg" sx={{ pt: 4 }}>
								File Attached
							</Typography>
						)}
						<Grid xs={12}>
							<FormControl>
								<FormLabel>Work Performed</FormLabel>
								<Textarea minRows={5} value={ticket.workPerformed} disabled={ticket.submitted || effectiveRole !== "Admin"} onChange={(event) => setTicket({ ...ticket, workPerformed: event.target.value })} />
							</FormControl>
						</Grid>

						{isPrint && (
							<>
								<Grid sx={{ pageBreakBefore: "always", justifyContent: "center" }} xs={12}>
									<FormLabel sx={{ typography: "h2", fontWeight: "bold", justifyContent: "center" }}>Legal</FormLabel>
								</Grid>
								<Grid xs={12} sx={{ justifyContent: "center" }}>
									<b>Dynamic Elevator Control Corp.</b> assumes no responsibility for any part of the elevator equipment except the upon which work has been done under this contract. No work service examination or liability on our part other than that specifically mentioned herein is included or intended. It is agreed that <b>Dynamic Elevator Control Corp.</b> does not assume possession or control of any part of the equipment and that such remains yours exclusively as the owner lessor, lessee or manager thereof.
								</Grid>
								<Grid xs={12} sx={{ justifyContent: "center" }}>
									<b>Dynamic Elevator Control Corp.</b> shall not be held responsible for any loss, damage, injury, delay or detention of persons or property caused by acts of government, civil or military authorities, war, civil commotion, theft, vandalism, labor troubles, explosion, fire, power failure, water damage, storms, lighting, natural or public catastrophe, acts of god, or by any other cause which is unavailable or beyond our reasonable control, and, in any event, we shall not be liable for consequential damages. It is agreed that in consideration of the services
									enumerated herein that nothing in this agreement shall be construed to mean that <b>Dynamic Elevator Control Corp.</b> or its representatives assumes any liability whatsoever on account of accidents, injuries, delay or detention to persons or property. The purchaser shall remain solely liable for any and all actions or claims for damages to persons property while riding on, being in or about the elevator or the associated areas regardless of whether such actions result from the use, operation, maintenance or condition of the elevator, machine rooms, hatchway
									or any of their components parts and the Purchaser does hereby further unconditionally agree to indemnify and hold harmless <b>Dynamic Elevator Control Corp.</b> and its officers, directors and employees from any and all claims as enumerated above, including, but not limited to any demands, judgments, awards liabilities, costs, attorney's fees or any other damages on account of any such actions or claims regardless of the cause of said actions and regardless of any negligence upon the part of <b>Dynamic Elevator Control Corp.</b>
								</Grid>
								<Grid xs={12} sx={{ justifyContent: "center" }}>
									Purchaser shall shut down the equipment immediately upon manifestation of any irregularities in operation or appearance in the equipment, notify <b>Dynamic Elevator Control Corp.</b> at once and keep equipment shut down until the completion of the repairs and further the Purchase shall keep the equipment under continued surveillance by competent personal to detect such irregularities. The Purchaser shall give <b>Dynamic Elevator Control Corp.</b> notice within ten days after occurrence of any accident in or about the elevator.{" "}
								</Grid>
								<Grid xs={12} sx={{ justifyContent: "center" }}>
									Purchaser agrees to pay for work performed upon presentation of invoice.
								</Grid>
								<Grid xs={12} sx={{ justifyContent: "center" }}>
									Nonpayment of any sum under this agreement shall be considered a material breach thereof. In the event of a breach of this agreement or the failure to pay any sum due under this agreement within thirty (30) days from the billing date. <b>Dynamic Elevator Control Corp.</b> may, at its option, declare all sums due or to become due under this agreement due payable in full as and for agreed and liquidate damages, and not as a penalty, and until the same are paid. <b>Dynamic Elevator Control Corp.</b> shall be discharged and released from any obligation and/or
									liability under the terms of this agreement.
								</Grid>
								<Grid xs={12} sx={{ justifyContent: "center" }}>
									In the event any attorney is engaged to enforce construe or defend any of the terms and conditions of this agreement or to collect any payment or lost profits due here under either with or without suit, the Purchaser agrees to pay all attorney's fees and costs incurred by <b>Dynamic Elevator Control Corp.</b>{" "}
								</Grid>
								<Grid xs={12} sx={{ justifyContent: "center" }}>
									The Purchaser does hereby waive trial by jury and does further hereby consent to the venue of any proceeding or lawsuit under this agreement to be in the County of Broward and State of Florida other county of <b>Dynamic Elevator Control Corp.</b> choice.{" "}
								</Grid>
								<Grid xs={12} sx={{ justifyContent: "center" }}>
									No agent or employee shall have the authority to waive or modify any of the above terms of this agreement.
								</Grid>
							</>
						)}
					</Grid>
				</DialogContent>
				{!isPrint && (
					<DialogActions>
						<Box sx={{ padding: 2 }}>
							<Button size="lg" color="primary" onClick={close} sx={{ mx: 1 }}>
								Close
							</Button>
							<Button size="lg" color="primary" onClick={printTicket} sx={{ mx: 1 }}>
								Print
							</Button>
							{!ticket.submitted && effectiveRole === "Admin" && (
								<>
									<Button size="lg" color="primary" onClick={() => save(false)} sx={{ mx: 1 }}>
										Save
									</Button>
									<Button size="lg" color="primary" onClick={() => save(true)} sx={{ mx: 1 }}>
										Submit
									</Button>
								</>
							)}
						</Box>
					</DialogActions>
				)}
			</>
		);
	};

	return (
		<>
			{!isPrint ? (
				<>
					<Drawer onClose={() => props.close()} anchor="right" open={open} size="lg">
						{render()}
					</Drawer>
				</>
			) : (
				<Dialog fullScreen open={open}>
					{render()}
				</Dialog>
			)}
		</>
	);
}
